// Offerings module endpoint functions.
//
// Read endpoints return view-model types (Offering, OfferingListItem).
// Mutation payloads (UpsertOfferingPayload) are camelCase — admin-service
// accepts that shape directly so we send it as-is.

import { adminApi as api } from '@core/api/httpClient'
import apiEndpoints from '@core/config/apiEndpoints'
import type { PaginatedResponse } from '@shared/types/pagination'
import type { OfferingStatusCountsDto } from './offeringContracts'
import type {
  ApproveOfferingOutcome,
  GetAllOfferingsParams,
  Offering,
  OfferingAvailableProperty,
  OfferingListItem,
  OfferingPropertyFinancials,
  UpsertOfferingPayload,
  VaspListing,
  OfferingWhitepaper,
  OfferingTransactions,
  OfferingHolders,
} from '../types/offeringTypes'
import {
  parseAllOfferingsList,
  parseApproveOfferingOutcome,
  parseOffering,
  parseOfferingListItem,
  parseOfferingStatusCounts,
  parsePropertyFinancials,
  parseVaspListing,
  parseOfferingWhitepaper,
  parseOfferingTransactions,
  parseOfferingHolders,
} from './offeringParser'

const OFFERINGS = '/api/admin/offerings'
const AVAILABLE_PROPERTIES = `${OFFERINGS}/available-properties`
// Served by the orders controller (orders imports offerings, never the reverse),
// but the data is the offering's — hence these endpoints live with the offering.
const OFFERING_TRANSACTIONS = (offeringId: string) =>
  `/api/admin/orders/offerings/${offeringId}/transactions`
const OFFERING_HOLDERS = (offeringId: string) => `/api/admin/orders/offerings/${offeringId}/holders`

export const getPropertyOfferings = async (propertyId: string): Promise<Offering[]> => {
  const { data } = await api.get(`${apiEndpoints.PROPERTIES}/${propertyId}/offerings`)
  const rows = Array.isArray(data?.data) ? data.data : []
  return rows.map(parseOffering)
}

export const addPropertyOffering = async (
  _propertyId: string,
  payload: UpsertOfferingPayload,
): Promise<{ id: string }> => {
  // API uses POST /api/admin/offerings with propertyId in the body.
  // Response is the created offering row (camelCase) — we only need its id.
  const { data } = await api.post(OFFERINGS, payload)
  const created = data?.data ?? data
  return { id: String(created?.id ?? '') }
}

export const updatePropertyOffering = async (
  _propertyId: string,
  offeringId: string,
  payload: UpsertOfferingPayload,
): Promise<unknown> => {
  const { data } = await api.patch(`${OFFERINGS}/${offeringId}`, payload)
  return data
}

// Single offering by id — powers the read-only Offering Details page.
// GET /api/admin/offerings/:id returns the camelCase OfferingRowResponseDto
// directly (the response interceptor adds the { success, data } envelope),
// so the row sits at data.data and needs no parsing.
export const getOfferingById = async (offeringId: string): Promise<OfferingListItem> => {
  const { data } = await api.get(`${OFFERINGS}/${offeringId}`)
  return parseOfferingListItem(data?.data ?? data)
}

// Financials carry the AED fee amounts used to pre-fill the offering's Fee
// Structure section. Response is camelCase.
export const getPropertyFinancials = async (id: string): Promise<OfferingPropertyFinancials> => {
  const { data } = await api.get(`${apiEndpoints.PROPERTIES}/${id}/financials`)
  return parsePropertyFinancials(data?.data ?? data)
}

export const getAvailableProperties = async (): Promise<OfferingAvailableProperty[]> => {
  // The backend (offerings.controller findAvailableProperties) returns the full
  // set via getMany() with no pagination — there's no limit param to pass.
  const { data } = await api.get(AVAILABLE_PROPERTIES)
  const envelope = data?.data ?? data
  const rows: Record<string, unknown>[] = Array.isArray(envelope)
    ? envelope
    : Array.isArray(envelope?.rows)
      ? envelope.rows
      : []
  return rows
    .map((p) => {
      const area = p.builtUpAreaSqm ?? p.built_up_area_sqm
      return {
        id: String(p.id ?? ''),
        name: String(p.property_name ?? p.propertyName ?? p.name ?? p.title ?? p.id ?? ''),
        builtUpAreaSqm: area == null || area === '' ? null : Number(area),
      }
    })
    .filter((p) => p.id)
}

// ── Offering status actions ───────────────────────────────────────────────────

export const submitOffering = async (offeringId: string): Promise<void> => {
  await api.patch(`${OFFERINGS}/${offeringId}/submit`)
}

// Approving also creates the Ctrl-Alt listing and submits it for the DLD value
// check. Both provider steps are best-effort server-side, so a VASP failure comes
// back in the body — with the same sentence the row persists — rather than as a
// rejected promise. Callers must read `vaspListing`, not just the resolved promise.
export const approveOffering = async (offeringId: string): Promise<ApproveOfferingOutcome> => {
  const { data } = await api.patch(`${OFFERINGS}/${offeringId}/approve`)
  return parseApproveOfferingOutcome(data?.data ?? data)
}

export const rejectOffering = async (offeringId: string, reason: string): Promise<void> => {
  await api.patch(`${OFFERINGS}/${offeringId}/reject`, { reason })
}

/**
 * Retry the VASP hand-off for an approved offering whose listing never made it.
 *
 * One action for what used to be two (Send to VASP / Submit for value check):
 * admin-service resolves from the stored state whether to create, replace or just
 * submit the listing. Answers 200 with the same outcome body as approve even when
 * the retry itself failed — read `vaspListing`, not the resolved promise.
 */
export const retryOfferingVaspHandoff = async (
  offeringId: string,
): Promise<ApproveOfferingOutcome> => {
  const { data } = await api.patch(`${OFFERINGS}/${offeringId}/retry-vasp`)
  return parseApproveOfferingOutcome(data?.data ?? data)
}

// Send a submitted (in_verification) offering back to draft during admin review,
// with a revision note for the creator. Used in place of rejection on Pending Approval.
export const revertOfferingToDraft = async (offeringId: string, reason: string): Promise<void> => {
  await api.patch(`${OFFERINGS}/${offeringId}/revert-to-draft`, { reason })
}

// Refresh the VASP listing status for an approved offering. When VASP reports
// the listing is live the offering flips to OPEN and leaves the onboarding queue.
export const syncOfferingVasp = async (offeringId: string): Promise<void> => {
  await api.patch(`${OFFERINGS}/${offeringId}/sync-vasp`)
}

/**
 * Register a filled listing's trade with DLD (filled → awaiting_approval).
 *
 * The one leg of a capital raise Ctrl-Alt leaves to the broker: once the raise is
 * filled, nothing at the provider moves until this is called, and it allows any gap
 * between the last order and the registration — which is why this is an operator
 * action and not something the filled webhook triggers.
 *
 * Unlike approve and retry, the step runs inside the request and answers 204: a
 * resolved promise means it landed. DLD's own approve / reject decision arrives
 * later as a webhook.
 */
export const registerOfferingVaspTrade = async (
  offeringId: string,
  acknowledgeNotReady = false,
): Promise<void> => {
  // LST-04: admin-service now runs its own readiness pre-flight and answers 409
  // VASP_LISTING_ORDERS_NOT_READY_FOR_REGISTRATION when an order on the listing has
  // not reached awaiting_issuance. The flag is how an operator who has been SHOWN
  // that shortfall registers anyway — the local order mirror trails a webhook, so a
  // stale count must not be able to strand a listing Ctrl-Alt would accept.
  await api.patch(`${OFFERINGS}/${offeringId}/vasp/listing/register-trade`, {
    acknowledgeNotReady,
  })
}

// The stored Ctrl-Alt listing for an offering, incl. the last webhook event/error.
// ── Directory sweep (cross-module lookups) ──────────────────────────────────
//
// A consumer that needs "the offering behind this property" for a table of rows
// cannot ask per row without a request per row, and cannot ask for one huge page
// without betting on a server-side `limit` ceiling nothing else in this app
// exceeds. So sweep: one request for page 1, then the rest in parallel off the
// page count it reports — the same shape `getPendingApprovalQueue` uses.

const OFFERINGS_LOOKUP_PAGE_SIZE = 100

// Hard stop. A deployment with more offerings than this needs a real per-id
// lookup endpoint, not a deeper sweep; the consumer renders the misses as
// unknown rather than firing an unbounded fan-out.
const OFFERINGS_LOOKUP_MAX_PAGES = 5

/**
 * Every offering the directory will return, up to the sweep cap.
 *
 * Deliberately unfiltered. Narrowing by `offeringType` server-side means a row
 * the backend left untyped disappears from the lookup entirely, and the consumer
 * cannot tell that apart from "this property has no offering" — the caller picks
 * the primary one itself, where it can fall back.
 */
export const getAllOfferingsForLookup = async (): Promise<OfferingListItem[]> => {
  const base: GetAllOfferingsParams = {
    limit: OFFERINGS_LOOKUP_PAGE_SIZE,
    sortBy: 'created_at',
    sortOrder: 'DESC',
  }

  const firstPage = await getAllOfferings({ ...base, page: 1 })
  const pageCount = Math.min(firstPage.pagination.totalPages || 1, OFFERINGS_LOOKUP_MAX_PAGES)

  const remainingPages = await Promise.all(
    Array.from({ length: Math.max(pageCount - 1, 0) }, (_, index) =>
      getAllOfferings({ ...base, page: index + 2 }),
    ),
  )

  return [firstPage, ...remainingPages].flatMap((page) => page.data)
}

export const getOfferingVaspListing = async (offeringId: string): Promise<VaspListing> => {
  const { data } = await api.get(`${OFFERINGS}/${offeringId}/vasp/listing`)
  return parseVaspListing(data?.data ?? data)
}

// Temporary escape hatch — skip VASP onboarding and force the offering live (OPEN).
export const bypassOfferingVasp = async (offeringId: string, reason?: string): Promise<void> => {
  await api.patch(`${OFFERINGS}/${offeringId}/bypass-vasp`, reason ? { reason } : {})
}

// ── Status counts ───────────────────────────────────────────────────────────
// Powers the sidebar work-queue badges (OfferingStatusCountsDto in
// offeringContracts.ts). Each field mirrors one offerings queue's list filter
// so the badge equals that queue's row count.
export const getOfferingStatusCounts = async (): Promise<OfferingStatusCountsDto> => {
  const { data } = await api.get(`${OFFERINGS}/status-counts`)
  return parseOfferingStatusCounts(data?.data ?? data)
}

export const getAllOfferings = async (
  params: GetAllOfferingsParams,
): Promise<PaginatedResponse<OfferingListItem>> => {
  const { data } = await api.get(OFFERINGS, { params })
  return parseAllOfferingsList(data)
}

// ── Offering record (details page) ──────────────────────────────────────────

/** Every money movement on the offering and its property, newest first. */
export const getOfferingTransactions = async (
  offeringId: string,
): Promise<OfferingTransactions> => {
  const { data } = await api.get(OFFERING_TRANSACTIONS(offeringId))
  return parseOfferingTransactions(data?.data ?? data)
}

/** The investors this offering issued tokens to, and the rent it paid them. */
export const getOfferingHolders = async (offeringId: string): Promise<OfferingHolders> => {
  const { data } = await api.get(OFFERING_HOLDERS(offeringId))
  return parseOfferingHolders(data?.data ?? data)
}

// ── Instrument documents (whitepaper) ───────────────────────────────────────

/**
 * What we have MIRRORED for the offering's instrument — not a vendor call.
 *
 * An empty `documents` is a steady state: the whitepaper exists only once the
 * instrument's review has passed.
 */
export const getOfferingWhitepaper = async (offeringId: string): Promise<OfferingWhitepaper> => {
  const { data } = await api.get(`${OFFERINGS}/${offeringId}/vasp/whitepaper`)
  return parseOfferingWhitepaper(data?.data ?? data)
}

/**
 * Fetch the instrument's documents from Ctrl-Alt and store them.
 *
 * Returns what is on record afterwards, so the caller reads the RESULT rather
 * than assuming the fetch produced something — a run that found nothing still
 * resolves 200, which is the common outcome while a review is outstanding.
 */
export const fetchOfferingWhitepaper = async (offeringId: string): Promise<OfferingWhitepaper> => {
  const { data } = await api.post(`${OFFERINGS}/${offeringId}/vasp/whitepaper/fetch`)
  return parseOfferingWhitepaper(data?.data ?? data)
}
